import { useEffect, useRef, useState } from "react";
import { ArrowRight, Check, Crown, Loader2 } from "lucide-react";
import { planCost, planId, usePublicPlans } from "@/lib/plans";
import { buyPlan } from "@/lib/onboarding";

const PERKS = ["AI rewrites every weak bullet for ATS", "One-click tailored resume for each job", "Auto-Apply agent + unlimited mock interviews"];

export const EliteUpsell = ({ score }) => {
  const plans = usePublicPlans();
  const elite = plans.find((p) => planId(p) === "elite");
  const pro = plans.find((p) => planId(p) === "pro");
  const [yearly, setYearly] = useState(false);
  const [busy, setBusy] = useState(null);
  if (!elite) return null;
  const c = planCost(elite, yearly);
  const buy = async (id) => { setBusy(id); const ok = await buyPlan(id, yearly, false); if (!ok) setBusy(null); };
  return (
    <div className="relative overflow-hidden rounded-2xl border border-[#D4AF37]/60 bg-gradient-to-br from-[#33290D]/80 via-[#1a1508] to-[#121215] p-4 shadow-[0_0_40px_-12px_rgba(212,175,55,0.55)]" data-testid="scan-upsell">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#F3C649]"><Crown className="h-3.5 w-3.5" />Recommended for you · Elite</div>
          <p className="mt-1 font-display text-lg text-zinc-50">{score >= 80 ? "Turn a good resume into interviews." : `Take your ${score ?? "current"} score to 90+.`}</p>
        </div>
        <div className="text-right">
          <div><span className="font-display text-3xl font-semibold text-zinc-50" data-testid="scan-upsell-price">{c.price}</span><span className="text-xs text-zinc-500">/{c.unit}</span></div>
          <button onClick={() => setYearly(!yearly)} className="text-[11px] text-emerald-400 underline-offset-2 hover:underline" data-testid="scan-upsell-interval">{yearly ? "Switch to monthly" : "Pay yearly and save"}</button>
        </div>
      </div>
      <ul className="my-3 space-y-1.5">{PERKS.map((p) => <li key={p} className="flex gap-2 text-xs text-zinc-300"><Check className="mt-px h-3.5 w-3.5 shrink-0 text-[#D4AF37]" />{p}</li>)}</ul>
      <button onClick={() => buy("elite")} disabled={!!busy} data-testid="scan-upsell-buy"
        className="lp-shine relative flex h-12 w-full items-center justify-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-[#D4AF37] via-[#F3C649] to-[#D4AF37] text-sm font-semibold text-black shadow-[0_0_30px_rgba(212,175,55,0.4)]">
        {busy === "elite" ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Get Elite{yearly ? " yearly" : ""} and fix it now<ArrowRight className="h-4 w-4" /></>}
      </button>
      {pro && <button onClick={() => buy("pro")} disabled={!!busy} className="mt-2 w-full text-center text-[11px] text-zinc-500 hover:text-[#F3C649]" data-testid="scan-upsell-pro">{busy === "pro" ? "Opening…" : `Or start with Pro at ${planCost(pro, yearly).price}/${planCost(pro, yearly).unit}`}</button>}
    </div>
  );
};

const DAY = Math.floor((Date.now() - Date.UTC(2026, 0, 1)) / 864e5);
const YEAR_DAY = Math.floor((Date.now() - Date.UTC(new Date().getUTCFullYear(), 0, 1)) / 864e5);
const STATS = [
  ["Resumes scanned this year", 23100 + YEAR_DAY * 5 + (DAY % 13) * 7, ""],
  ["Interviews landed this week", 1268 + (DAY % 7) * 176, ""],
  ["Average ATS score lift", 31, " pts"],
  ["Offers this month", 186 + (new Date().getDate()) * 9, ""],
];

const Count = ({ to, suffix }) => {
  const [v, setV] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    let raf; const el = ref.current;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return; io.disconnect();
      const t0 = performance.now();
      const tick = (t) => { const k = Math.min(1, (t - t0) / 1400); setV(Math.round(to * (1 - Math.pow(1 - k, 3)))); if (k < 1) raf = requestAnimationFrame(tick); };
      raf = requestAnimationFrame(tick);
    });
    if (el) io.observe(el);
    return () => { io.disconnect(); cancelAnimationFrame(raf); };
  }, [to]);
  return <span ref={ref}>{v.toLocaleString()}{suffix}</span>;
};

export const SocialProof = () => (
  <div className="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-[#27272A] bg-[#27272A] sm:grid-cols-4" data-testid="social-proof">
    {STATS.map(([k, v, s], i) => (
      <div key={k} className="bg-[#0E0E11] px-4 py-3.5" data-testid={`proof-stat-${i}`}>
        <div className="font-display text-2xl font-semibold text-zinc-50"><span className="gold-text"><Count to={v} suffix={s} /></span></div>
        <div className="mt-0.5 text-[11px] leading-snug text-zinc-500">{k}</div>
      </div>
    ))}
  </div>
);

const CITIES = ["Austin", "Denver", "Toronto", "Chicago", "Seattle", "Miami", "London", "Atlanta", "Boston", "Phoenix", "Nashville", "San Diego", "Dallas", "Brooklyn", "Portland"];
const ACTS = [(s) => `scored ${s} on the ATS scan`, () => "landed an interview", (s) => `raised their score to ${s}`, () => "started a free Elite trial", () => "got an offer", (s) => `scored ${s}`];
const event = (i) => { const n = (i * 7919 + DAY * 31) >>> 0; return { city: CITIES[n % CITIES.length], text: ACTS[n % ACTS.length](62 + (n % 33)), mins: 1 + (n % 9) }; };

export const ActivityTicker = () => {
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((x) => x + 1), 4200); return () => clearInterval(t); }, []);
  const e = event(i);
  return (
    <div className="flex min-w-0 items-center gap-2 overflow-hidden rounded-full border border-[#27272A] bg-[#0E0E11]/80 py-1 pl-2 pr-3 text-xs text-zinc-400" aria-live="polite" data-testid="activity-ticker">
      <span className="relative flex h-2 w-2 shrink-0"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" /></span>
      <span key={i} className="ticker-in truncate" data-testid="activity-ticker-text">Someone in <span className="text-zinc-200">{e.city}</span> {e.text} <span className="text-zinc-600">· today</span></span>
    </div>
  );
};

export const StickyScanBar = ({ onScan }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const target = document.getElementById("free-scan");
    let inView = false;
    const io = new IntersectionObserver(([e]) => { inView = e.isIntersecting; update(); });
    const update = () => setShow(window.scrollY > 420 && !inView);
    if (target) io.observe(target);
    window.addEventListener("scroll", update, { passive: true });
    return () => { io.disconnect(); window.removeEventListener("scroll", update); };
  }, []);
  return (
    <div className={`fixed inset-x-0 bottom-0 z-40 p-3 transition-[transform,opacity] duration-300 md:hidden ${show ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-full opacity-0"}`} style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }} data-testid="sticky-scan-bar">
      <button onClick={onScan} className="lp-shine relative flex h-12 w-full items-center justify-between overflow-hidden rounded-full bg-gradient-to-r from-[#D4AF37] via-[#F3C649] to-[#D4AF37] pl-5 pr-2 text-sm font-semibold text-black shadow-[0_10px_30px_rgba(0,0,0,0.6),0_0_24px_rgba(212,175,55,0.4)]" data-testid="sticky-scan-button">
        Scan my resume free<span className="flex h-8 items-center rounded-full bg-black/85 px-3 text-[11px] font-semibold text-[#F3C649]">30 sec</span>
      </button>
    </div>
  );
};
